import { EncoderDecoder } from '@iotize/common/converter/api';
import { combineLatest, Observable, Subject } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';

import { TapDataError } from '../tap-data-error';

type RawType = Uint8Array;

export type KeyTypeType = string | number | symbol;

export interface EditableRawDataStreamInterface<
  RawType,
  KeyType extends KeyTypeType,
> {
  /**
   * Stream identifier
   */
  id: KeyType;

  /**
   * Last raw value or undefined
   */
  rawValueSnaphot: RawType | undefined;

  /**
   * Raw values observable
   */
  rawValues: Observable<RawType>;

  /**
   * Read raw value.
   * It may also trigger a value changed event
   */
  readRaw(): Promise<RawType>;

  /**
   * Write raw value
   * @param data
   */
  writeRaw(data: RawType): Promise<void>;

  /**
   * Notify observers that value has changed
   * Both rawValues and values observable will be notified
   * @param newValue
   */
  notifyRawValue(newValue: RawType): void;
}

export interface EditableValueDataStreamInterface<
  DataType,
  KeyType extends KeyTypeType,
> {
  /**
   * Stream identifier
   */
  id: KeyType;

  /**
   * Last value or undefined
   */
  valueSnapshot: DataType | undefined;

  /**
   * Values observable
   */
  values: Observable<DataType>;

  /**
   * Read value.
   * It may also trigger a value changed event
   */
  read(): Promise<DataType>;

  /**
   * Write given value
   * @param value
   */
  write(value: DataType): Promise<any>;
}

/**
 * Data stream that can be edited (with read/write raw function)
 */
export interface EditableDataStreamInterface<
  DataType,
  RawType,
  KeyType extends KeyTypeType,
> extends EditableRawDataStreamInterface<RawType, KeyType>,
    EditableValueDataStreamInterface<DataType, KeyType> {}

export type RawBundleType<DataType> = Record<
  keyof DataType,
  Uint8Array | undefined
>;
export interface BundleDataStreamInterface<
  DataType extends Record<KeyTypeType, any>,
  // RawType extends Record<keyof DataType, Uint8Array | undefined> = Record<keyof DataType, Uint8Array | undefined>
> extends EditableDataStreamInterface<
    DataType,
    RawBundleType<DataType>,
    keyof DataType
  > {
  // valueSnapshot: Partial<DataType>;

  variables: EditableDataStreamInterface<
    DataType[keyof DataType],
    Uint8Array,
    keyof DataType
  >[];

  variable(
    id: keyof DataType,
  ): EditableDataStreamInterface<
    DataType[keyof DataType],
    Uint8Array,
    keyof DataType
  >;
}

export abstract class AbstractBundleDataStream<
  DataType extends Record<KeyTypeType, any>,
> implements
    BundleDataStreamInterface<Record<keyof DataType, DataType[keyof DataType]>>
{
  abstract id: KeyTypeType;

  get variables() {
    return Object.values(this._variables);
  }

  constructor(
    private _variables: Record<
      keyof DataType,
      EditableDataStreamInterface<
        DataType[keyof DataType],
        Uint8Array,
        keyof DataType
      >
    >, // private _variables: EditableDataStreamInterface<any, Uint8Array>[] // converter: EncoderDecoder<DataType, Uint8Array>
  ) {}

  abstract readRawInner(): Promise<RawBundleType<DataType>>;

  async readRaw(): Promise<RawBundleType<DataType>> {
    const raw = await this.readRawInner();
    this.notifyRawValue(raw);
    return raw;
  }
  // abstract read(): Promise<DataType>;

  async read(): Promise<DataType> {
    await this.readRaw();
    return this.valueSnapshot;
  }

  async write(value: DataType): Promise<void> {
    // if (typeof value !== 'object') {
    //     throw TapDataError.illegalParameter();
    // }
    for (const key in value) {
      await this.variable(key).writeRaw(value[key]);
    }
  }

  variable<Key extends keyof DataType>(
    id: Key,
  ): EditableDataStreamInterface<DataType[Key], Uint8Array, Key> {
    if (!(id in this._variables)) {
      throw new Error(
        `Variable "${id}" not found in bundle "${this.id.toString()}"`,
      );
    }
    return (this._variables as any)[id];
  }

  get rawValues(): Observable<RawBundleType<DataType>> {
    const variables = Object.values(this._variables);
    return combineLatest(
      variables.map((v) => v.rawValues.pipe(startWith(undefined))),
    ).pipe(
      map((values) => {
        return values.reduce<any>((accumulator, v, index) => {
          accumulator[variables[index].id] = v as any; // TODO
          return accumulator;
        }, {});
      }),
    );
  }

  get rawValueSnaphot(): RawBundleType<DataType> {
    const variables = Object.values(this._variables);
    return variables.reduce<any>((accumulator, v, index) => {
      accumulator[variables[index].id] = v.rawValueSnaphot;
      return accumulator;
    }, {});
  }

  get valueSnapshot(): DataType {
    const variables = Object.values(this._variables);
    return variables.reduce<any>((accumulator, v, index) => {
      accumulator[variables[index].id] = v.valueSnapshot;
      return accumulator;
    }, {});
  }

  get values(): Observable<DataType> {
    const variables = Object.values(this._variables);
    return combineLatest(
      variables.map((v) => v.values.pipe(startWith(undefined))),
    ).pipe(
      map((values) => {
        return values.reduce<Partial<DataType>>((accumulator, v, index) => {
          accumulator[variables[index].id] = v;
          return accumulator;
        }, {}) as DataType;
      }),
    );
  }

  async notifyRawValue(v: RawBundleType<DataType>) {
    for (const id in v) {
      const value = v[id];
      if (value !== undefined) {
        this._variables[id]?.notifyRawValue(value as RawType);
      }
    }
  }

  findVariable(id: keyof DataType) {
    return id in this._variables ? this._variables[id] : undefined;
  }

  hasVariable(id: keyof DataType) {
    return !!this.findVariable(id);
  }

  async writeRaw(values: RawBundleType<DataType>): Promise<void> {
    for (const key in values) {
      const value = values[key];
      if (value !== undefined) {
        await this.variable(key).writeRaw(value as RawType);
      }
    }
  }
}

export abstract class AbstractEditableDataStream<
  DataType,
  RawType = Uint8Array,
  KeyType extends KeyTypeType = KeyTypeType,
> implements EditableDataStreamInterface<DataType, RawType, KeyType>
{
  abstract id: KeyType;
  private _lastRawValue?: RawType;

  abstract converter: EncoderDecoder<DataType, RawType>;

  get lastRawValue(): RawType | undefined {
    return this._lastRawValue;
  }

  // static fromCustom(arg0: CustomVariableConfig) {
  //     return new Variable<T>(config, );
  // }

  // public static fromConfig<T>(config: VariableConfig) {
  //     return new Variable<T>(config, );
  // }

  // public static fromMemory(config: MemoryInfoConfig) {
  //     return new Variable<T>(config, );
  // }

  protected rawDataStream = new Subject<RawType>(); // = new Subject<RawType>();
  // private selfReadStream = new Subject<RawType>();

  public readonly values: Observable<DataType> = this.rawDataStream.pipe(
    map((v) => this.decodeValue(v)),
  );

  constructor() /** // private monitoringController: Observable<{}> // public context: ContextType, // public config: ConfigType, // dataStream?: Observable<RawType> | undefined,
   * New value stream
   * Usefull if you want to update
   */ {
    // if (dataStream) {
    //     this.dataStream = merge(
    //         dataStream,
    //         this.selfReadStream
    //     );
    // }
    // else {
    //     this.dataStream = this.selfReadStream;
    // }
    // this.dataStream = this.dataStream
    //     .pipe(
    //         tap(v => this.lastRawValue = v)
    //     );
    // // Do we need to subscribe ?
    // this.dataStream.subscribe();
  }

  abstract readRawInner(): Promise<RawType>;
  abstract writeRaw(value: RawType): Promise<void>;

  get rawValueSnaphot() {
    return this.lastRawValue;
  }

  get valueSnapshot(): DataType | undefined {
    if (this.lastRawValue === undefined) {
      return undefined;
    }
    return this.decodeValue(this.lastRawValue);
  }

  get rawValues(): Observable<RawType> {
    return this.rawDataStream;
  }
  //  {
  //     const value = await this.rawReader(this.context);
  //     this.notifyRawValue(value);
  //     return value;
  // }

  async notifyRawValue(newValue: RawType) {
    this._lastRawValue = newValue;
    this.rawDataStream.next(newValue);
  }

  async readRaw(): Promise<RawType> {
    const value = await this.readRawInner();
    this.notifyRawValue(value);
    return value;
  }

  async read(): Promise<DataType> {
    const p = this.values.pipe(first()).toPromise() as Promise<DataType>;
    await this.readRaw();
    return p;
  }

  async write(value: DataType): Promise<any> {
    // if (!this.rawWritter) {
    //     throw new Error(`Cannot write value for ${this.id}`);
    // }
    const encoded = this.encodeValue(value);
    return this.writeRaw(encoded);
  }

  private encodeValue(value: DataType): RawType {
    try {
      return this.converter.encode(value);
    } catch (err) {
      throw TapDataError.unexpectedEncodedValueError(this, value, err);
    }
  }

  private decodeValue(data: RawType) {
    try {
      return this.converter.decode(data);
    } catch (err) {
      throw TapDataError.unexpectedVariableDataRead(this, data, err);
    }
  }
}
